import React from 'react';
import { Button, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';

/**
 * Styled Waltero Button with brand-specific styling and enhanced hover states
 */
const StyledWalteroButton = styled(Button)(({ theme, walteroVariant, size }) => {
  const baseStyles = {
    borderRadius: '8px',
    textTransform: 'uppercase',
    fontFamily: '"Mozaic Geo", "Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
    fontWeight: 500,
    letterSpacing: '0.01em',
    transition: 'all 0.2s ease-in-out',
    boxShadow: 'none',
    
    // Size variations
    ...(size === 'small' && {
      padding: '8px 16px',
      fontSize: '0.875rem'
    }),
    ...(size === 'medium' && {
      padding: '12px 24px',
      fontSize: '1rem'
    }),
    ...(size === 'large' && {
      padding: '16px 32px',
      fontSize: '1.125rem'
    })
  };

  // Waltero-specific variants
  const variantStyles = {
    primary: {
      backgroundColor: theme.palette.waltero.secondary.primaryApricot,
      color: theme.palette.waltero.white,
      boxShadow: '0px 2px 8px rgba(241, 136, 73, 0.25)',
      '&:hover': {
        backgroundColor: theme.palette.waltero.secondary.darkApricot,
        boxShadow: '0px 4px 16px rgba(195, 93, 44, 0.35)',
        transform: 'translateY(-1px)'
      },
      '&:active': {
        transform: 'translateY(0px)',
        boxShadow: '0px 2px 8px rgba(195, 93, 44, 0.4)'
      },
      '&:disabled': {
        backgroundColor: theme.palette.waltero.grey[300],
        color: theme.palette.waltero.grey[500],
        boxShadow: 'none',
        transform: 'none'
      }
    },
    
    secondary: {
      backgroundColor: theme.palette.waltero.primary.lightBlue,
      color: theme.palette.waltero.white,
      boxShadow: '0px 2px 8px rgba(61, 141, 174, 0.25)',
      '&:hover': {
        backgroundColor: theme.palette.waltero.primary.mediumBlue,
        boxShadow: '0px 4px 16px rgba(35, 103, 139, 0.35)',
        transform: 'translateY(-1px)'
      },
      '&:active': {
        transform: 'translateY(0px)',
        boxShadow: '0px 2px 8px rgba(35, 103, 139, 0.4)'
      },
      '&:disabled': {
        backgroundColor: theme.palette.waltero.grey[300],
        color: theme.palette.waltero.grey[500],
        boxShadow: 'none',
        transform: 'none'
      }
    },
    
    outlined: {
      backgroundColor: 'transparent',
      color: theme.palette.waltero.primary.lightBlue,
      border: `2px solid ${theme.palette.waltero.primary.lightBlue}`,
      '&:hover': {
        backgroundColor: theme.palette.waltero.primary.lightBlue,
        color: theme.palette.waltero.white,
        borderColor: theme.palette.waltero.primary.lightBlue,
        transform: 'translateY(-1px)',
        boxShadow: '0px 4px 16px rgba(61, 141, 174, 0.25)'
      },
      '&:active': {
        transform: 'translateY(0px)'
      },
      '&:disabled': {
        backgroundColor: 'transparent',
        color: theme.palette.waltero.grey[400],
        borderColor: theme.palette.waltero.grey[300],
        transform: 'none'
      }
    },
    
    text: {
      backgroundColor: 'transparent',
      color: theme.palette.waltero.primary.mediumBlue,
      '&:hover': {
        backgroundColor: `${theme.palette.waltero.primary.paleBlue}30`,
        color: theme.palette.waltero.primary.coreBlue,
        transform: 'translateY(-1px)'
      },
      '&:active': {
        transform: 'translateY(0px)'
      },
      '&:disabled': {
        backgroundColor: 'transparent',
        color: theme.palette.waltero.grey[400],
        transform: 'none'
      }
    },
    
    danger: {
      backgroundColor: theme.palette.waltero.secondary.darkApricot,
      color: theme.palette.waltero.white,
      boxShadow: '0px 2px 8px rgba(195, 93, 44, 0.25)',
      '&:hover': {
        backgroundColor: '#a34a23',
        boxShadow: '0px 4px 16px rgba(195, 93, 44, 0.4)',
        transform: 'translateY(-1px)'
      },
      '&:active': {
        transform: 'translateY(0px)'
      },
      '&:disabled': {
        backgroundColor: theme.palette.waltero.grey[300],
        color: theme.palette.waltero.grey[500],
        boxShadow: 'none',
        transform: 'none'
      }
    },
    
    success: {
      backgroundColor: theme.palette.success.main,
      color: theme.palette.waltero.white,
      boxShadow: '0px 2px 8px rgba(76, 175, 80, 0.25)',
      '&:hover': {
        backgroundColor: '#43a047',
        boxShadow: '0px 4px 16px rgba(76, 175, 80, 0.35)',
        transform: 'translateY(-1px)'
      },
      '&:active': {
        transform: 'translateY(0px)'
      },
      '&:disabled': {
        backgroundColor: theme.palette.waltero.grey[300],
        color: theme.palette.waltero.grey[500],
        boxShadow: 'none',
        transform: 'none'
      }
    }
  };

  return {
    ...baseStyles,
    ...variantStyles[walteroVariant || 'primary']
  };
});

/**
 * WalteroButton - CTA buttons with proper hover states
 * 
 * A branded button component following Waltero design guidelines with
 * enhanced hover effects, proper typography, and semantic color variants.
 * 
 * @param {Object} props - Component properties
 * @param {React.ReactNode} props.children - Button content
 * @param {string} [props.walteroVariant='primary'] - Button variant ('primary', 'secondary', 'outlined', 'text', 'danger', 'success')
 * @param {string} [props.size='medium'] - Button size ('small', 'medium', 'large')
 * @param {boolean} [props.disabled=false] - Whether button is disabled
 * @param {boolean} [props.fullWidth=false] - Whether button should take full width
 * @param {React.ReactNode} [props.startIcon] - Icon to display at start of button
 * @param {React.ReactNode} [props.endIcon] - Icon to display at end of button
 * @param {Function} [props.onClick] - Click handler
 * @param {string} [props.className] - Additional CSS classes
 * @param {Object} [props.sx] - Additional MUI sx props
 */
const WalteroButton = ({
  children,
  walteroVariant = 'primary',
  size = 'medium',
  disabled = false,
  fullWidth = false,
  startIcon,
  endIcon,
  onClick,
  className,
  sx,
  ...otherProps
}) => {
  const theme = useTheme();
  
  // Filter out custom props to avoid DOM warnings
  const { walteroVariant: _, ...filteredProps } = otherProps;
  
  return (
    <StyledWalteroButton
      walteroVariant={walteroVariant}
      size={size}
      disabled={disabled}
      fullWidth={fullWidth}
      startIcon={startIcon}
      endIcon={endIcon}
      onClick={onClick}
      className={className}
      sx={sx}
      {...filteredProps}
    >
      {children}
    </StyledWalteroButton>
  );
};

export default WalteroButton;